import React, { useEffect, useState } from  "react";
import { isValid, isValidArray, isValidString } from "../../utils/utilities";
import { QRCodeSVG } from 'qrcode.react';
import './ticket-summary.style.scss';
import { roundNumber} from './../../utils/math.utils';
import { getTicketById } from './../../actions/ticket.action';
import { getSeatLayOut } from './../../actions/admin.action';
import { incrementDays, setTimeToDateObject, subractTime } from './../../utils/date.utils';
import { logoStyle, PRIVILEGE_HEAD_OFFICE } from "../../utils/constant";
import qwikBusIcon from './../../images/qwikbus-logo.png';

// Same logo precedence as AppHeader (app-header.component.js)
const getLogoUrl = () => {
    if(global.travelsDetails&&global.travelsDetails.print_logo){
        return global.travelsDetails.print_logo;
    }else{
        return global.logoUrl || qwikBusIcon;
    }
};

const getGstNumber=(schedule,route,travels)=>{
    if(isValid(route.gst_number)){
        return route.gst_number;
    }else if(isValid(schedule.gst_number)){
        return schedule.gst_number;
    }else{
        return travels.gst_number
    }
}

const getRefundAmount=(travellers)=>{
    let refundAmount=0 , isCancelledTravellerPresent=false;
    travellers.forEach(element => {
        if(element.traveller_status === "Cancelled"){
            isCancelledTravellerPresent=true;
            refundAmount = refundAmount+(element.amount_paid-element.cancellation_penalty);
        }
    });
    return {refundAmount,isCancelledTravellerPresent}
}

const formatAmount=(value)=>{
    let amount = Number(roundNumber(value));
    if(isNaN(amount)){
        return '';
    }
    return amount.toLocaleString('en-IN',{minimumFractionDigits:2,maximumFractionDigits:2});
}

const getBoardingMoment=(ticketInfo)=>{
    let {tripRoute,pickupDetails} = ticketInfo;
    if(!isValidArray(pickupDetails)||!isValid(pickupDetails.timing)||!isValid(tripRoute.start_date)){
        return null;
    }
    let boardingMoment = setTimeToDateObject(tripRoute.start_date,pickupDetails.timing);
    if(pickupDetails.origin_day>0){
        boardingMoment = incrementDays(boardingMoment,pickupDetails.origin_day);
    }
    return boardingMoment;
}

const getDropMoment=(ticketInfo)=>{
    let {tripRoute,dropDetails,route} = ticketInfo;
    if(!isValidArray(dropDetails)||!isValid(dropDetails.timing)||!isValid(tripRoute.start_date)){
        return null;
    }
    let dayValue = 0;
    if(route.destination_day>route.origin_day){
        dayValue = route.destination_day-route.origin_day;
    }
    if(dropDetails.destination_day>0){
        dayValue = dayValue + dropDetails.destination_day;
    }
    let dropMoment = setTimeToDateObject(tripRoute.start_date,dropDetails.timing);
    if(dayValue>0){
        dropMoment = incrementDays(dropMoment,dayValue);
    }
    return dropMoment;
}

const getJourneyDuration=(boardingMoment,dropMoment)=>{
    if(!isValid(boardingMoment)||!isValid(dropMoment)){
        return null;
    }
    let minutes = dropMoment.diff(boardingMoment,'minutes');
    if(minutes<=0){
        return null;
    }
    let hours = Math.floor(minutes/60);
    let mins = minutes%60;
    return `${hours<10?'0'+hours:hours}:${mins<10?'0'+mins:mins} Hrs`;
}

const isEmptySeat=(seat)=>seat.seat_number==='Empty'||seat.seat_number==='empty';

// Same rule as the booking layout (seat-layout-container.module.js): a sleeper
// with a non-zero seat_orientation lies across the column axis, so in the
// transposed mini map it is drawn tall instead of wide. isValid(0) is false,
// so isValid alone means "non-null and non-zero".
const isRotatedSleeper=(seat)=>isValid(seat)&&seat.sleeper===1&&isValid(seat.seat_orientation);

const ToiletIcon=()=>(
    <svg className="tp-feature-icon" viewBox="0 0 24 24" aria-label="Toilet" role="img">
        <path d="M6.5 3a1.6 1.6 0 1 1 0 3.2A1.6 1.6 0 0 1 6.5 3Zm11 0a1.6 1.6 0 1 1 0 3.2 1.6 1.6 0 0 1 0-3.2ZM5 7h3c.83 0 1.5.67 1.5 1.5V13H8.4v7H7v-5.5h-1V20H4.6v-7H3.5V8.5C3.5 7.67 4.17 7 5 7Zm11.2 0h2.6c.62 0 1.17.4 1.36.99L21.5 13h-1.9l1 4h-1.85v3h-1.5v-3H15.4l1-4h-1.9l1.34-5.01c.19-.59.74-.99 1.36-.99Z"/>
        <rect x="11.55" y="2.5" width="0.9" height="19" rx="0.45"/>
    </svg>
);

/* Side of the cell a Rear/Side exit's floating red line goes to: right (rear of
   the transposed map) for Rear, the window edge for Side (bottom on the first
   column strip, top on the last). Empty for Roof and unset types. */
const getExitEdgeClass=(seat,minCol)=>{
    if(seat.feature_type==='Rear'){
        return ' tp-exit-rear';
    }
    if(seat.feature_type==='Side'){
        return seat.seat_column_number===minCol?' tp-exit-side-bottom':' tp-exit-side-top';
    }
    return '';
};

/* Roof gets its chip icon with a caret on each side of it; Rear/Side are marked
   by the floating red line instead. */
const ExitMarker=({seat})=>{
    if(seat.feature_type==='Roof'){
        return (
            <span className="tp-roof-marker">
                <span className="tp-exit-caret tp-caret-left"/>
                <span className="tp-roof-chip"/>
                <span className="tp-exit-caret tp-caret-right"/>
            </span>
        );
    }
    return null;
};

/* The booking-page layout (generateSeatLayout in seat-layout.module.js) drawn
   horizontally: each seat_column_number is one horizontal strip stacked
   bottom-to-top starting at column 1, seat_row_number runs left-to-right. */
const MiniSeatMap=({seats,bookedSeatNumbers})=>{

    if(!isValidArray(seats)||seats.length===0){
        return null;
    }

    let decks={upper:[],lower:[]};
    seats.forEach((seat)=>{
        if(seat.upper_deck){
            decks.upper.push(seat);
        }else{
            decks.lower.push(seat);
        }
    });

    const renderDeck=(deckSeats,label)=>{
        if(deckSeats.filter((seat)=>!isEmptySeat(seat)).length===0){
            return null;
        }
        let grid={} , maxRowByCol={} , minCol=Number.MAX_SAFE_INTEGER , maxCol=0;
        deckSeats.forEach((seat)=>{
            minCol=Math.min(minCol,seat.seat_column_number);
            maxCol=Math.max(maxCol,seat.seat_column_number);
            if(!isValid(maxRowByCol[seat.seat_column_number])||maxRowByCol[seat.seat_column_number]<seat.seat_row_number){
                maxRowByCol[seat.seat_column_number]=seat.seat_row_number;
            }
            if(!isValidArray(grid[seat.seat_row_number])){
                grid[seat.seat_row_number]={};
            }
            grid[seat.seat_row_number][seat.seat_column_number]=seat;
        });

        let strips=[];
        for(let col=minCol;col<=maxCol;col++){
            let cells=[];
            let rowCount=isValid(maxRowByCol[col])?maxRowByCol[col]:0;
            for(let row=1;row<=rowCount;row++){
                let seat=isValidArray(grid[row])?grid[row][col]:undefined;
                if(isValid(seat)&&isEmptySeat(seat)&&isValid(seat.seat_feature)){
                    // Featured empty positions (Toilet / Emergency Exit) show as a
                    // cell instead of a gap: toilet with its icon (sized by its
                    // sleeper footprint like a seat). A Rear/Side exit is a red
                    // line floating outside the cell on the side it opens to —
                    // right (rear of the transposed map) for Rear, the window
                    // edge for Side — and Roof gets its chip.
                    let isToilet=seat.seat_feature==='Toilet';
                    let rotated=isRotatedSleeper(seat);
                    let exitEdgeClass=isToilet?'':getExitEdgeClass(seat,minCol);
                    let featureClass=isToilet?'tp-seat-toilet':(exitEdgeClass?`tp-seat-gap${exitEdgeClass}`:'tp-seat-exit');
                    cells.push(<span key={`feature-${col}-${row}`}
                        title={seat.seat_feature+(isValid(seat.feature_type)?` - ${seat.feature_type}`:'')}
                        className={`tp-seat-cell ${featureClass} ${rotated?'tp-seat-sleeper-vertical':(seat.sleeper===1?'tp-seat-sleeper':'')}`}>
                        {isToilet?<ToiletIcon/>:<ExitMarker seat={seat}/>}
                    </span>);
                }else if(!isValid(seat)||isEmptySeat(seat)){
                    // Same rule as generateSeatLayout: a gap between two
                    // sleeper strips takes a sleeper-sized slot (rotated
                    // sleepers are only one cell wide, so they don't count)
                    let prevSeat=isValidArray(grid[row])?grid[row][col-1]:undefined;
                    let nextSeat=isValidArray(grid[row])?grid[row][col+1]:undefined;
                    let isSleeperGap=(isValid(seat)&&seat.sleeper===1&&!isRotatedSleeper(seat))||
                        (isValid(prevSeat)&&prevSeat.sleeper===1&&!isRotatedSleeper(prevSeat)&&
                            isValid(nextSeat)&&nextSeat.sleeper===1&&!isRotatedSleeper(nextSeat));
                    let isRotatedGap=!isSleeperGap&&isRotatedSleeper(seat);
                    cells.push(<span key={`gap-${col}-${row}`} className={`tp-seat-cell tp-seat-gap ${isSleeperGap?'tp-seat-sleeper':''} ${isRotatedGap?'tp-seat-sleeper-vertical':''}`} />);
                }else{
                    let rotated=isRotatedSleeper(seat);
                    let isExitSeat=seat.seat_feature==='Emergency Exit';
                    let booked=bookedSeatNumbers.indexOf(seat.seat_number)!==-1;
                    // A Rear/Side exit seat keeps its normal look: the red line
                    // outside the cell is the marker
                    let exitEdgeClass=isExitSeat?getExitEdgeClass(seat,minCol):'';
                    cells.push(
                        <span key={seat.seat_number}
                            title={isExitSeat?`Emergency Exit${isValid(seat.feature_type)?` - ${seat.feature_type}`:''}`:undefined}
                            className={`tp-seat-cell ${rotated?'tp-seat-sleeper-vertical':(seat.sleeper===1?'tp-seat-sleeper':'')} ${booked?'tp-seat-booked':''} ${isExitSeat&&!exitEdgeClass?'tp-seat-exit':''}${exitEdgeClass}`}
                        >
                            {booked&&<span className="tp-seat-num">{seat.seat_number}</span>}
                            {isExitSeat&&<ExitMarker seat={seat}/>}
                        </span>
                    );
                }
            }
            strips.push(<div key={`strip-${col}`} className="tp-strip">{cells}</div>);
        }

        return (
            <div className="tp-deck">
                <div className="tp-deck-label">{label}</div>
                <div className="tp-deck-strips">{strips}</div>
            </div>
        )
    }

    return (
        <div className="tp-seatmap">
            {renderDeck(decks.upper,'Upper')}
            {renderDeck(decks.lower,'Lower')}
        </div>
    )
}

export const TicketSummary = ({ticketId,ticketDetails,isPrinter=false}) => {

    const [ticketInfo,setTicketInfo] = useState(undefined);
    const [loading,setLoading] = useState(false);
    const [layoutSeats,setLayoutSeats] = useState(null);

    const onSuccess =(data)=>{
        setTicketInfo(data);
        setLoading(false);
    }

    const onFailure=()=>{
        setLoading(false);
    }

    useEffect(()=>{
        if(isValidArray(ticketDetails)&&isValidArray(ticketDetails.ticket)&&isValid(ticketDetails.ticket.id)){
            setTicketInfo(ticketDetails);
        }else{
            if(isValid(ticketId)){
                setLoading(true);
                getTicketById(ticketId,onSuccess,onFailure)
            }
        }
    },[ticketId,ticketDetails]);

    // The bus seat layout (same API as /admin/vehicle-management) is used for
    // the mini seat map, so it works from every screen that renders the ticket
    let layoutBusId = isValidArray(ticketInfo)&&isValidArray(ticketInfo.bus)&&isValid(ticketInfo.bus.id)?ticketInfo.bus.id:
        (isValidArray(ticketInfo)&&isValidArray(ticketInfo.trip)?ticketInfo.trip.bus_id:null);

    useEffect(()=>{
        if(isValid(layoutBusId)){
            getSeatLayOut(layoutBusId,(data)=>{
                if(isValidArray(data)&&isValidArray(data.seat_layout)&&data.seat_layout.length>0){
                    setLayoutSeats(data.seat_layout);
                }else{
                    setLayoutSeats(null);
                }
            },()=>{
                setLayoutSeats(null);
            });
        }
    },[layoutBusId]);

    if(!loading&&isValidArray(ticketInfo)&&isValidArray(ticketInfo.ticket)&&isValid(ticketInfo.ticket.id)){
        let {
            schedule,
            ticket,
            agent,
            traveller,
            travels,
            route,
            bus,
            pickupDetails,
            dropDetails
        } = ticketInfo;

        let gstNumber=getGstNumber(schedule,route,travels);
        let printLogo=getLogoUrl();
        let webSiteUrl=travels.website_url;
        let operatorName=global.operatorName;


        let hideGender = isValid(global.travelsAppConfig)&&isValid(global.travelsAppConfig['HIDE_GENDER_FROM_BOOKING'])&&global.travelsAppConfig['HIDE_GENDER_FROM_BOOKING']==1;
        let hideAge = isValid(global.travelsAppConfig)&&isValid(global.travelsAppConfig['HIDE_AGE_FROM_BOOKING'])&&global.travelsAppConfig['HIDE_AGE_FROM_BOOKING']==1;

        let boardingMoment = getBoardingMoment(ticketInfo);
        let dropMoment = getDropMoment(ticketInfo);
        let journeyDuration = getJourneyDuration(boardingMoment,dropMoment);
        let reportingMoment = isValid(boardingMoment)?subractTime(boardingMoment,0,5):null;

        let bookedSeatNumbers=[] , seatNumbersText='';
        if(isValidArray(traveller)){
            traveller.forEach((element)=>{
                bookedSeatNumbers.push(element.seat_number);
            });
            seatNumbersText = bookedSeatNumbers.join(', ');
        }

        let {isCancelledTravellerPresent , refundAmount} = getRefundAmount(traveller);

        let bookedTimeText = '';
        if(isValid(ticket.booking_time)){
            // Same "07/09/2026 2:00 PM" shape as the travels-generated ticket pdf
            bookedTimeText = new Date(ticket.booking_time).toLocaleString('en-IN',{
                timeZone:'Asia/Kolkata', day:'2-digit', month:'2-digit', year:'numeric',
                hour:'numeric', minute:'2-digit', hour12:true
            }).replace(',','').toUpperCase();
        }
        let bookedByText = `${agent.name}@${bookedTimeText}`;

        let gstAmount = roundNumber((isValid(ticket.sgst)?Number(ticket.sgst):0)+(isValid(ticket.cgst)?Number(ticket.cgst):0));
        let isDiscountExits=isValid(ticket.direct_discount)&&ticket.direct_discount>0?true:false;

        let fullWebsiteUrl = isValidString(webSiteUrl)?(webSiteUrl.startsWith('http')?webSiteUrl:`https://${webSiteUrl}`):null;
        let qrValue = null;
        if(isValidString(fullWebsiteUrl)){
            qrValue = `${fullWebsiteUrl}/manage-booking/ticket?pnr=${ticket.pnr}&phone_number=${ticket.phone_number}`;
        }

        let websiteDiscountPercentage = isValid(ticketInfo.websiteDiscountPercentage)?Number(ticketInfo.websiteDiscountPercentage):0;
        let showWebsiteOffer = global.privilege === PRIVILEGE_HEAD_OFFICE&&isValidString(webSiteUrl)&&websiteDiscountPercentage>0;

        let boardingPhone = isValidArray(pickupDetails)&&isValid(pickupDetails.phone_number)?pickupDetails.phone_number:'';
        let dropPhone = isValidArray(dropDetails)&&isValid(dropDetails.phone_number)?dropDetails.phone_number:'';
        // Same as travels ticket pdf/sms - agent's phone replaces boarding/drop phone when show_phone is on
        if(isValid(agent)&&agent.show_phone&&isValidString(agent.phone_number)){
            boardingPhone = agent.phone_number;
            dropPhone = agent.phone_number;
        }

        return (
            <div id="ticketSummary" className="tp-ticket">
                <div className="tp-topbar" />
                <div className="tp-header">
                    <div className="tp-header-left">
                        {isValidString(printLogo)?
                            <img src={printLogo} alt={operatorName} style={logoStyle} />
                            :
                            <div className="tp-operator-name-fallback">{operatorName}</div>
                        }
                        {isValidString(webSiteUrl)&&
                            <div className="tp-website-link">{webSiteUrl.replace(/^https?:\/\//,'')}</div>
                        }
                    </div>
                    <div className="tp-header-center">
                        <div className="tp-operator-name">{operatorName}</div>
                        {isValidString(travels.address)&&
                            <div className="tp-operator-address">{travels.address.replace(/##|@@/g,' ')}</div>
                        }
                        {isValid(gstNumber)&&
                            <div className="tp-gstn">GSTN : {gstNumber}</div>
                        }
                        {isValidArray(global.travelsDetails)&&isValid(global.travelsDetails.phone_number)&&
                            <div className="tp-operator-phone">PH : {global.travelsDetails.phone_number}</div>
                        }
                    </div>
                    <div className="tp-header-right">
                        {isValid(qrValue)&&
                            <QRCodeSVG value={qrValue} size={72} />
                        }
                    </div>
                </div>
                <div className="tp-banner">
                    <span className="tp-banner-star">★</span>
                    <span className="tp-banner-text">Journey Details</span>
                    <span className="tp-banner-star">★</span>
                </div>
                <div className="tp-summary-row">
                    <div className="tp-summary-left">
                        <div className="tp-pnr-row">
                            <span className="tp-pnr-label">PNR</span>
                            <span className="tp-pnr-value">{ticket.pnr}</span>
                        </div>
                        {isValid(reportingMoment)&&
                            <div className="tp-summary-item">
                                <div className="tp-summary-item-label">Reporting Time</div>
                                <div className="tp-summary-item-value">{reportingMoment.format('h:mm A')}&nbsp;&nbsp;{reportingMoment.format('MMM D, YYYY')}</div>
                            </div>
                        }
                        <div className="tp-summary-item">
                            <div className="tp-summary-item-label">Bus Type</div>
                            <div className="tp-summary-item-value">{bus.description}</div>
                        </div>
                    </div>
                    <div className="tp-summary-right">
                        <MiniSeatMap seats={layoutSeats} bookedSeatNumbers={bookedSeatNumbers} />
                        <div className="tp-seat-no">Seat No&nbsp;&nbsp;<span>{seatNumbersText}</span></div>
                    </div>
                </div>
                <div className="tp-journey">
                    <div className="tp-journey-top">
                        <div className="tp-journey-origin">
                            <div className="tp-city">{route.origin}</div>
                            {isValid(boardingMoment)&&
                                <div className="tp-journey-time">{boardingMoment.format('MMM DD, YYYY h:mm A')}</div>
                            }
                            <div className="tp-place-label">BOARDING PLACE</div>
                        </div>
                        <div className="tp-journey-middle">
                            {isValid(journeyDuration)&&
                                <div className="tp-duration">{journeyDuration}</div>
                            }
                        </div>
                        <div className="tp-journey-destination">
                            <div className="tp-city">{route.destination}</div>
                            {isValid(dropMoment)&&
                                <div className="tp-journey-time">{dropMoment.format('MMM DD, YYYY h:mm A')}</div>
                            }
                            <div className="tp-place-label">DROPPING PLACE</div>
                        </div>
                    </div>
                    <div className="tp-journey-places">
                        <div className="tp-boarding-place">
                            {isValidArray(pickupDetails)&&
                                <>
                                    <div className="tp-place-name">{pickupDetails.location}{isValidString(boardingPhone)?` (${boardingPhone})`:''}</div>
                                    {isValidString(pickupDetails.address)&&
                                        <div className="tp-place-address">{pickupDetails.address}</div>
                                    }
                                    {isValidString(pickupDetails.landmark)&&
                                        <div className="tp-place-address">{pickupDetails.landmark}</div>
                                    }
                                </>
                            }
                        </div>
                        <div className="tp-dropping-place">
                            {isValidArray(dropDetails)&&
                                <>
                                    <div className="tp-place-name">{dropDetails.location}{isValidString(dropPhone)?` (${dropPhone})`:''}</div>
                                    {isValidString(dropDetails.address)&&
                                        <div className="tp-place-address">{dropDetails.address}</div>
                                    }
                                    {isValidString(dropDetails.landmark)&&
                                        <div className="tp-place-address">{dropDetails.landmark}</div>
                                    }
                                </>
                            }
                        </div>
                    </div>
                </div>
                <div className="tp-details-row">
                    <div className="tp-passengers">
                        <div className="tp-passengers-header">Passenger Details</div>
                        {isValidArray(traveller)&&traveller.map((element)=>{
                            let passengerMeta=[];
                            if(!hideAge&&isValid(element.age)){
                                passengerMeta.push(element.age);
                            }
                            if(!hideGender&&isValidString(element.gender)){
                                passengerMeta.push(element.gender.charAt(0));
                            }
                            let metaText = passengerMeta.length>0?` (${passengerMeta.join(', ')})`:'';
                            let isCancelled = element.traveller_status==='Cancelled';
                            return (
                                <div className={`tp-passenger-row ${isCancelled?'tp-passenger-cancelled':''}`} key={element.seat_number}>
                                    <span className="tp-passenger-name">{element.full_name}{metaText}{isCancelled?' - Cancelled':''}</span>
                                    <span className="tp-passenger-seat">{element.seat_number}</span>
                                </div>
                            )
                        })}
                    </div>
                    <div className="tp-fare">
                        <div className="tp-fare-row">
                            <span className="tp-fare-label">Fare</span>
                            <span className="tp-fare-colon">:</span>
                            <span className="tp-fare-value">{formatAmount(ticket.fare)}</span>
                        </div>
                        {isDiscountExits&&
                            <div className="tp-fare-row">
                                <span className="tp-fare-label">Discount</span>
                                <span className="tp-fare-colon">:</span>
                                <span className="tp-fare-value">- {formatAmount(ticket.direct_discount)}</span>
                            </div>
                        }
                        {gstAmount>0&&
                            <div className="tp-fare-row">
                                <span className="tp-fare-label">GST</span>
                                <span className="tp-fare-colon">:</span>
                                <span className="tp-fare-value">{formatAmount(gstAmount)}</span>
                            </div>
                        }
                        <div className="tp-fare-row tp-fare-total">
                            <span className="tp-fare-label">Total Amount</span>
                            <span className="tp-fare-colon">:</span>
                            <span className="tp-fare-value">{formatAmount(ticket.amount_paid)}</span>
                        </div>
                        {isCancelledTravellerPresent&&
                            <>
                                <div className="tp-fare-row">
                                    <span className="tp-fare-label">Can. Penalty</span>
                                    <span className="tp-fare-colon">:</span>
                                    <span className="tp-fare-value">{formatAmount(ticket.cancellation_penalty)}</span>
                                </div>
                                <div className="tp-fare-row">
                                    <span className="tp-fare-label">Refund</span>
                                    <span className="tp-fare-colon">:</span>
                                    <span className="tp-fare-value">{formatAmount(refundAmount)}</span>
                                </div>
                            </>
                        }
                    </div>
                </div>
                <div className="tp-footer-row">
                    <div className="tp-footer-terms">
                        <div className="tp-footer-label">Terms &amp; Conditions</div>
                        {isValidString(webSiteUrl)&&
                            <div className="tp-footer-value">{`${fullWebsiteUrl}/tandc`}</div>
                        }
                    </div>
                    <div className="tp-footer-booked">
                        <div className="tp-footer-label">Booked By</div>
                        <div className="tp-footer-value">{bookedByText}</div>
                    </div>
                </div>
                <div className="tp-tracking-note">
                    Vehicle tracking details will be shared 4 hours before your boarding time.
                </div>
                <div className="tp-thankyou">
                    {showWebsiteOffer&&
                        <div className="tp-website-offer">
                            {`Book directly through ${operatorName} Website (${webSiteUrl.replace(/^https?:\/\//,'')}) and avail an exclusive ${roundNumber(websiteDiscountPercentage)}% off.`}
                        </div>
                    }
                    <div className="tp-thankyou-text">
                        Thank you for choosing us to be a part of your journey!<br/>
                        Have A Safe Journey!
                    </div>
                    {isValidString(webSiteUrl)&&!showWebsiteOffer&&
                        <div className="tp-thankyou-website">{webSiteUrl.replace(/^https?:\/\//,'')}</div>
                    }
                </div>
            </div>
        )
    }else{
        return (<div></div>)
    }
}
